import { useState } from 'react';
import { MAX_GUILD_EMOJIS, MAX_GUILD_STICKERS } from '@comms/shared';
import { useStore } from '../../store';
import { api, uploadFile } from '../../lib/api';
import { Button, ErrorText, Input } from '../ui';

const NO_EMOJI: never[] = [];

function ImageUpload({ label, onPicked }: { label: string; onPicked: (f: File | null) => void }) {
  return (
    <label className="block text-sm text-text-2">
      {label}
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={(e) => onPicked(e.target.files?.[0] ?? null)}
        className="mt-1 block text-sm"
      />
    </label>
  );
}

export function EmojiSettings({ guildId }: { guildId: string }) {
  const emojis = useStore((s) => s.guilds[guildId]?.emojis ?? NO_EMOJI);
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function add() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const a = await uploadFile(file, file.name);
      await api('POST', `/guilds/${guildId}/emojis`, { name, attachmentId: a.id });
      setName('');
      setFile(null);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-text-0">Emoji</h2>
      <p className="text-sm text-text-3">
        Up to {MAX_GUILD_EMOJIS} custom emoji. PNG, JPEG, WebP or GIF up to 512 KB; images are
        resized to 128px. Use them as <code>:name:</code> in messages and reactions.
      </p>
      <div className="flex flex-wrap items-end gap-3 rounded bg-bg-1 p-3">
        <ImageUpload label="Image" onPicked={setFile} />
        <div className="w-48">
          <Input
            label="Name"
            value={name}
            placeholder="party_parrot"
            onChange={(e) => setName(e.target.value.replace(/[^A-Za-z0-9_]/g, '_'))}
          />
        </div>
        <Button disabled={busy || !file || name.length < 2} onClick={add}>
          Upload emoji
        </Button>
      </div>
      <ErrorText error={error} />
      <div className="divide-y divide-bg-1">
        {emojis.length === 0 && <p className="text-sm text-text-3">No custom emoji yet.</p>}
        {emojis.map((e) => (
          <div key={e.id} className="flex items-center gap-3 py-2">
            <img src={e.url} alt={`:${e.name}:`} className="h-8 w-8 object-contain" />
            <span className="flex-1 text-text-1">:{e.name}:</span>
            <Button
              variant="ghost"
              onClick={() => {
                const next = prompt('Emoji name', e.name);
                if (next && next !== e.name)
                  void api('PATCH', `/guilds/${guildId}/emojis/${e.id}`, { name: next }).catch(
                    setError,
                  );
              }}
            >
              Rename
            </Button>
            <Button
              variant="danger"
              title={`Delete :${e.name}:`}
              onClick={() =>
                confirm(`Delete :${e.name}:?`) &&
                void api('DELETE', `/guilds/${guildId}/emojis/${e.id}`).catch(setError)
              }
            >
              Delete
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function StickerSettings({ guildId }: { guildId: string }) {
  const stickers = useStore((s) => s.guilds[guildId]?.stickers ?? NO_EMOJI);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function add() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const a = await uploadFile(file, file.name);
      await api('POST', `/guilds/${guildId}/stickers`, {
        name,
        description,
        attachmentId: a.id,
      });
      setName('');
      setDescription('');
      setFile(null);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-text-0">Stickers</h2>
      <p className="text-sm text-text-3">
        Up to {MAX_GUILD_STICKERS} stickers. PNG, JPEG, WebP or GIF (animated works) up to 512 KB;
        resized to 320px.
      </p>
      <div className="flex flex-wrap items-end gap-3 rounded bg-bg-1 p-3">
        <ImageUpload label="Image" onPicked={setFile} />
        <div className="w-40">
          <Input label="Sticker name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="w-56">
          <Input
            label="Description"
            value={description}
            maxLength={100}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <Button disabled={busy || !file || name.trim().length < 2} onClick={add}>
          Upload sticker
        </Button>
      </div>
      <ErrorText error={error} />
      <div className="grid grid-cols-4 gap-3">
        {stickers.length === 0 && <p className="text-sm text-text-3">No stickers yet.</p>}
        {stickers.map((s) => (
          <div key={s.id} className="group relative rounded bg-bg-1 p-2 text-center">
            <img src={s.url} alt={s.name} className="mx-auto h-24 w-24 object-contain" />
            <div className="truncate text-sm text-text-1">{s.name}</div>
            <button
              type="button"
              title={`Delete ${s.name}`}
              onClick={() =>
                confirm(`Delete sticker "${s.name}"?`) &&
                void api('DELETE', `/guilds/${guildId}/stickers/${s.id}`).catch(setError)
              }
              className="absolute right-1 top-1 hidden rounded bg-danger px-1.5 text-xs text-white group-hover:block"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
